/**
 * Guest Checkout Modal - Class Booking Without Sign-up
 * Supports both first-class discount (new users) and regular pricing (returning users)
 * Streamlined checkout: Name, Email, Phone → Pay → Booked!
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, CreditCard, Clock, MapPin, Loader2, CheckCircle, Mail, Phone, User, Sparkles, Tag, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GuestCheckoutModal({ 
  isOpen, 
  onClose, 
  session,
  onSuccess 
}) {
  const [step, setStep] = useState('details'); // details, payment, success
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  
  // Coupon & referral state
  const [couponCode, setCouponCode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralDiscount, setReferralDiscount] = useState(null); // {code, discount_percent}
  const [referralApplied, setReferralApplied] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount_percent, title}
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  
  // Dynamic pricing state - initialize from session data
  const getInitialPricing = useCallback(() => {
    if (!session) return { isFirstClass: true, price: 0, priceDisplay: '', originalPrice: 0, discountText: '', isLoading: false };
    const pricePerClass = session.class_type?.price_per_class || session.price_inr;
    const offerPrice = session.class_type?.offer_price || session.offer_price;
    if (offerPrice) {
      const origPaise = pricePerClass ? Math.round(pricePerClass * 100) : Math.round(offerPrice * 200);
      const paise = Math.round(offerPrice * 100);
      const pct = origPaise > 0 ? Math.round((1 - paise / origPaise) * 100) : 0;
      return { isFirstClass: true, price: paise, priceDisplay: `₹${offerPrice.toLocaleString()}`, originalPrice: origPaise, discountText: pct > 0 ? `${pct}% off your first class!` : 'Special first class price!', isLoading: false };
    }
    if (pricePerClass) {
      const paise = Math.round(pricePerClass * 100);
      const halfPaise = Math.round(paise / 2);
      return { isFirstClass: true, price: halfPaise, priceDisplay: `₹${Math.round(pricePerClass / 2).toLocaleString()}`, originalPrice: paise, discountText: '50% off your first class!', isLoading: false };
    }
    return { isFirstClass: true, price: 0, priceDisplay: '', originalPrice: 0, discountText: '', isLoading: false };
  }, [session]);

  const [pricing, setPricing] = useState(getInitialPricing);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('details');
      setFormData({ name: '', email: '', phone: '' });
      setErrors({});
      setCouponCode('');
      setReferralCode('');
      setReferralDiscount(null);
      setReferralApplied(false);
      setReferralError('');
      setCouponApplied(null);
      setCouponError('');
      setPricing(getInitialPricing());
      
      // Auto-detect referral code from URL
      const urlRef = new URLSearchParams(window.location.search).get('ref');
      if (urlRef) {
        setReferralCode(urlRef.toUpperCase());
        setReferralLoading(true);
        // Validate and get discount
        fetch(`${API_URL}/api/referral/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: urlRef }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.valid && data.guest_discount_percent > 0) {
              setReferralDiscount({ code: data.code, discount_percent: data.guest_discount_percent });
              setReferralApplied(true);
              applyReferralOptimistic(data.code, data.guest_discount_percent);
            }
          })
          .catch(() => {})
          .finally(() => setReferralLoading(false));
      }
    }
  }, [isOpen, getInitialPricing]);

  // Fetch pricing when email or phone changes (debounced)
  // Both are checked server-side to determine first-class eligibility.
  const fetchPricing = useCallback(async (email, phone, appliedCoupon = null) => {
    if (!session?.id) return;
    const cleanedPhone = (phone || '').replace(/\D/g, '');
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    const phoneValid = /^[0-9]{10}$/.test(cleanedPhone);
    // Require at least one valid identifier to avoid spamming the endpoint
    if (!emailValid && !phoneValid) return;
    
    setPricing(prev => ({ ...prev, isLoading: true }));
    
    try {
      const body = { session_id: session.id };
      if (emailValid) body.email = email.trim().toLowerCase();
      if (phoneValid) body.phone = cleanedPhone;
      if (appliedCoupon) body.coupon_code = appliedCoupon;
      if (referralCode) body.referral_code = referralCode;
      
      const response = await fetch(`${API_URL}/api/guest/checkout/get-pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricing({
          isFirstClass: data.is_first_class,
          price: data.price,
          priceDisplay: data.price_display,
          originalPrice: data.original_price,
          discountText: data.discount_text,
          referralApplied: data.referral_applied || null,
          couponApplied: data.coupon_applied || null,
          payPerClassBlocked: !!data.pay_per_class_blocked,
          payPerClassMessage: data.pay_per_class_message || null,
          isLoading: false
        });
        // Keep the referralDiscount badge in sync with what the server
        // actually applied — if the server rejected the code (typo,
        // inactive, no settings) we shouldn't be showing the badge.
        if (data.referral_applied) {
          setReferralDiscount({
            code: data.referral_applied.code,
            discount_percent: data.referral_applied.discount_percent,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      setPricing(prev => ({ ...prev, isLoading: false }));
    }
  }, [session?.id, referralCode]);

  // Debounce email/phone input for pricing check
  useEffect(() => {
    const timer = setTimeout(() => {
      const emailOK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
      const phoneOK = /^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''));
      if (emailOK || phoneOK) {
        fetchPricing(formData.email, formData.phone, couponApplied?.code);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.email, formData.phone, fetchPricing, couponApplied]);
  
  // Apply coupon code
  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    
    setCouponError('');
    setCouponLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          email: formData.email?.trim().toLowerCase(),
          phone: formData.phone?.replace(/\D/g, ''),
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setCouponApplied({ code: data.code, discount_percent: data.discount_percent, title: data.title });
        setCouponError('');
        // Re-fetch pricing with coupon
        if (formData.email && formData.email.includes('@')) {
          fetchPricing(formData.email, formData.phone, data.code);
        }
      } else {
        setCouponError(data.detail || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };
  
  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
    // Re-fetch pricing without coupon
    if (formData.email && formData.email.includes('@')) {
      fetchPricing(formData.email, formData.phone, null);
    }
  };

  // Apply referral code manually
  // Apply the referral discount to the *current* pricing optimistically.
  // The backend recomputes once we have a valid email/phone, but the
  // user shouldn't have to wait for that — the moment they apply a
  // valid referral code we should reflect the new price in the Price
  // Summary so they see the value of the code immediately.
  // `code` is passed in (rather than read from state) so the callback
  // stays stable — referralCode's setter races with this call.
  const applyReferralOptimistic = useCallback((code, discountPct) => {
    if (!discountPct || discountPct <= 0) return;
    setPricing(prev => {
      // Prefer originalPrice (the MRP) — that's what the backend uses
      // as the base when a referral overrides first-class. Fall back
      // to the current price if originalPrice isn't populated yet
      // (e.g. session has no offer_price field).
      const basePaise = prev.originalPrice || prev.price || 0;
      if (basePaise <= 0) return prev;
      const newPaise = Math.round(basePaise * (1 - discountPct / 100));
      return {
        ...prev,
        // Referral overrides first-class: drop the flag so the UI
        // hides the "First Class" headline + price-summary row.
        isFirstClass: false,
        price: newPaise,
        priceDisplay: `₹${Math.round(newPaise / 100).toLocaleString()}`,
        originalPrice: basePaise,
        discountText: `Referral discount (${discountPct}% off)`,
        referralApplied: {
          code: code || '',
          discount_percent: discountPct,
          discount_amount: basePaise - newPaise,
        },
        // Clear any coupon visual artifacts — coupon + referral are
        // mutually exclusive on the server.
        couponApplied: null,
        isLoading: false,
      };
    });
  }, []);

  const handleApplyReferral = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) return;

    setReferralError('');
    setReferralLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/referral/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      if (response.ok && data?.valid) {
        setReferralDiscount({ code: data.code, discount_percent: data.guest_discount_percent || 0 });
        setReferralApplied(true);
        setReferralError('');
        // Apply the discount to the Price Summary immediately so the
        // user sees the value of the code without waiting for email
        // entry. The backend recomputes once email/phone are valid.
        applyReferralOptimistic(data.code, data.guest_discount_percent || 0);
        // Re-fetch pricing with referral (if email/phone are already
        // entered, this overrides the optimistic value with the
        // canonical server-side calculation).
        if (formData.email && formData.email.includes('@')) {
          fetchPricing(formData.email, formData.phone, couponApplied?.code);
        }
      } else {
        setReferralError(data?.detail || 'Invalid referral code');
        setReferralDiscount(null);
        setReferralApplied(false);
      }
    } catch {
      setReferralError('Failed to validate referral code');
    } finally {
      setReferralLoading(false);
    }
  };

  const handleRemoveReferral = () => {
    setReferralCode('');
    setReferralDiscount(null);
    setReferralApplied(false);
    setReferralError('');
    // Re-fetch pricing without referral if email is set, otherwise
    // reset to the initial first-class pricing so the Price Summary
    // doesn't keep showing the referral-discounted price.
    if (formData.email && formData.email.includes('@')) {
      fetchPricing(formData.email, formData.phone, couponApplied?.code);
    } else {
      setPricing(getInitialPricing());
    }
  };

  if (!isOpen || !session) return null;

  // Real-time validation helpers
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
  const isValidPhone = (v) => /^[0-9]{10}$/.test((v || '').replace(/\D/g, ''));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) return;
    if (isSubmitting) return;

    // Pay-per-class gate — refuse to launch Razorpay when the studio's
    // window is closed for this returning user.
    if (pricing?.payPerClassBlocked) {
      setErrors({ submit: pricing.payPerClassMessage || 'This class can be booked with SAMA credits only.' });
      return;
    }
    
    setIsSubmitting(true);
    setIsLoading(true);
    setErrors({});
    
    const email = formData.email.trim().toLowerCase();
    
    try {
      const orderBody = {
        session_id: session.id,
        name: formData.name.trim(),
        email: email,
        phone: formData.phone.replace(/\D/g, '')
      };
      if (couponApplied?.code) {
        orderBody.coupon_code = couponApplied.code;
      }
      if (referralCode.trim()) {
        orderBody.referral_code = referralCode.trim().toUpperCase();
      }
      
      const orderResponse = await fetch(`${API_URL}/api/guest/checkout/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderBody)
      });
      
      let orderData;
      try {
        orderData = await orderResponse.json();
      } catch (parseError) {
        console.error('Create order parse error');
        throw new Error('Server returned an invalid response. Please try again.');
      }
      
      if (!orderResponse.ok) {
        throw new Error(orderData?.detail || orderData?.message || 'Failed to create order. Please try again.');
      }
      
      setOrderData(orderData);
      
      // FREE booking: skip Razorpay, verify directly
      if (orderData.amount === 0) {
        await verifyFreeBooking(orderData);
      } else {
        openRazorpay(orderData);
      }
      
    } catch (error) {
      console.error('Create order error:', error);
      setErrors({ submit: error.message || 'Something went wrong. Please try again.' });
      setIsSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const verifyFreeBooking = async (order) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/guest/checkout/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `free_${Date.now()}`,
          razorpay_signature: 'free_booking',
          guest_token: order.guest_token
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Booking failed. Please contact support.');
      }
      
      if (!response.ok) {
        throw new Error(data?.detail || 'Booking failed');
      }
      
      setBookingResult(data);
      setStep('success');
      if (onSuccess) onSuccess(data);
    } catch (error) {
      setErrors({ submit: error.message || 'Free booking failed. Please try again.' });
      setIsSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const openRazorpay = (order) => {
    const options = {
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      name: 'SAMA Studio',
      description: order.is_first_class 
        ? `First Class: ${order.session_name}` 
        : `Class: ${order.session_name}`,
      order_id: order.order_id,
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      theme: {
        color: '#2F5D50'
      },
      handler: async function(response) {
        await verifyPayment(response, order);
      },
      modal: {
        ondismiss: function() {
          setStep('details');
          setIsSubmitting(false);
        }
      }
    };
    
    const razorpay = new window.Razorpay(options);
    razorpay.open();
    setStep('payment');
  };

  const verifyPayment = async (paymentResponse, order) => {
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await fetch(`${API_URL}/api/guest/checkout/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
          guest_token: order.guest_token
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Verify payment parse error');
        throw new Error('Payment verification failed. Please contact support.');
      }
      
      if (!response.ok) {
        throw new Error(data?.detail || data?.message || 'Payment verification failed');
      }
      
      setBookingResult(data);
      setStep('success');
      
      if (onSuccess) {
        onSuccess(data);
      }
      
    } catch (error) {
      console.error('Verify payment error:', error);
      setErrors({ submit: error.message || 'Payment verification failed. Please contact support.' });
      setStep('details');
      setIsSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeStr;
    }
  };

  const renderDetails = () => (
    <>
      {/* Session Info */}
      <div className="bg-stone-50 rounded-2xl p-4 mb-6">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Clock className="w-7 h-7 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-foreground mb-1">
              {session.class_type?.name || 'Class'}
            </h4>
            <p className="text-sm text-muted-foreground mb-1">
              {formatTime(session.start_time)}
            </p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              {session.studio?.name || 'Studio'}
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Price Indicator */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {pricing.referralApplied
              ? 'Class Price'
              : (pricing.isFirstClass ? 'First Class Price' : 'Class Price')}
          </span>
          {pricing.isLoading && (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          )}
        </div>
        {!pricing.isLoading && (
          <div className="text-right flex items-center gap-2">
            {pricing.originalPrice && pricing.originalPrice !== pricing.price && (
              <span className="text-sm text-muted-foreground line-through">
                ₹{(pricing.originalPrice / 100).toLocaleString()}
              </span>
            )}
            <span className="text-lg font-heading text-primary font-semibold">{pricing.priceDisplay}</span>
          </div>
        )}
      </div>

      {/* First Class Discount Badge — hidden when a referral takes
          precedence (the referral badge below will appear instead). */}
      {pricing.isFirstClass && !pricing.referralApplied && pricing.discountText && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-700 font-medium">{pricing.discountText}</span>
        </div>
      )}

      {/* Referral Discount Badge */}
      {referralDiscount && !couponApplied && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="referral-discount-badge">
          <Gift className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <span className="text-sm text-purple-700 font-medium">
            Referral discount: {referralDiscount.discount_percent}% off (code: {referralDiscount.code})
          </span>
        </div>
      )}

      {/* Returning User Notice */}
      {!pricing.isFirstClass && !pricing.isLoading && formData.email && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
          <p className="text-sm text-blue-700">
            <strong>Welcome back!</strong> Book at the regular class rate, or check out our 
            <a href="/packages" className="text-primary underline ml-1">class packages</a> for better value.
          </p>
        </div>
      )}

      {/* Form */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Your Name
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your full name"
              className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
                errors.name ? 'border-red-300 bg-red-50' : 'border-stone-200'
              } focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              data-testid="guest-name-input"
            />
          </div>
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                const v = e.target.value;
                setFormData({ ...formData, email: v });
                // Real-time validation: clear if valid, set message if user typed but invalid
                setErrors(prev => {
                  const next = { ...prev };
                  if (!v.trim()) { delete next.email; }
                  else if (isValidEmail(v)) { delete next.email; }
                  else { next.email = 'Please enter a valid email address'; }
                  return next;
                });
              }}
              placeholder="your@email.com"
              inputMode="email"
              autoComplete="email"
              className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
                errors.email ? 'border-red-300 bg-red-50' : 'border-stone-200'
              } focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              data-testid="guest-email-input"
            />
          </div>
          {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="guest-email-error">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                // Strip any non-digit character as it's typed; cap at 10 digits
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData({ ...formData, phone: digits });
                // Real-time validation
                setErrors(prev => {
                  const next = { ...prev };
                  if (!digits) { delete next.phone; }
                  else if (digits.length === 10) { delete next.phone; }
                  else { next.phone = 'Phone number must be 10 digits'; }
                  return next;
                });
              }}
              onKeyDown={(e) => {
                // Block non-numeric keystrokes (allow nav/backspace/tab/enter)
                const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];
                if (allowed.includes(e.key)) return;
                if (e.metaKey || e.ctrlKey) return;  // allow copy/paste
                if (!/^[0-9]$/.test(e.key)) e.preventDefault();
              }}
              placeholder="10-digit mobile number"
              inputMode="numeric"
              pattern="[0-9]{10}"
              maxLength={10}
              autoComplete="tel"
              className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
                errors.phone ? 'border-red-300 bg-red-50' : 'border-stone-200'
              } focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              data-testid="guest-phone-input"
            />
          </div>
          {errors.phone && <p className="text-red-500 text-xs mt-1" data-testid="guest-phone-error">{errors.phone}</p>}
        </div>
        
        {/* Coupon & Referral Code Section — always visible */}
        <div className="pt-2 space-y-3">
          {/* Coupon input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Coupon Code
            </label>
            {couponApplied ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-3" data-testid="coupon-applied-badge">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">{couponApplied.code}</span>
                  <span className="text-xs text-green-600">({couponApplied.discount_percent}% off)</span>
                </div>
                <button onClick={handleRemoveCoupon} className="text-xs text-red-500 hover:text-red-600 font-medium" data-testid="remove-coupon-btn">
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                    placeholder="Enter coupon code"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono uppercase"
                    data-testid="coupon-code-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  className="px-4 py-3 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-all"
                  data-testid="apply-coupon-btn"
                >
                  {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </button>
              </div>
            )}
            {couponError && <p className="text-red-500 text-xs mt-1" data-testid="coupon-error">{couponError}</p>}
          </div>
              
          {/* Referral code input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Referral Code <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            {referralApplied && referralDiscount ? (
              <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl p-3" data-testid="referral-applied-badge">
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">{referralDiscount.code}</span>
                  {referralDiscount.discount_percent > 0 && (
                    <span className="text-xs text-purple-600">({referralDiscount.discount_percent}% off)</span>
                  )}
                </div>
                <button onClick={handleRemoveReferral} className="text-xs text-red-500 hover:text-red-600 font-medium" data-testid="remove-referral-btn">
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralError(''); }}
                    placeholder="Friend's referral code"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono uppercase"
                    data-testid="referral-code-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyReferral}
                  disabled={referralLoading || !referralCode.trim()}
                  className="px-4 py-3 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-all"
                  data-testid="apply-referral-btn"
                >
                  {referralLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </button>
              </div>
            )}
            {referralError && <p className="text-red-500 text-xs mt-1" data-testid="referral-error">{referralError}</p>}
          </div>
        </div>
      </div>

      {errors.submit && (
        <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-xl">
          {errors.submit}
        </div>
      )}

      {/* Pay-per-class window block — replaces the pay CTA with a wallet
          redirect when the studio's PPC window is closed. Renders the
          spec copy verbatim. */}
      {pricing?.payPerClassBlocked && (
        <div
          className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 space-y-2"
          data-testid="pay-per-class-blocked"
        >
          <p>
            This class can be booked with SAMA credits only. To buy a membership package,{' '}
            <a
              href="/wallet"
              className="font-semibold underline text-primary hover:opacity-80"
              data-testid="pay-per-class-blocked-cta"
            >
              please click here
            </a>
            .
          </p>
        </div>
      )}

      {/* Price Summary Breakdown */}
      {!pricing.isLoading && pricing.price !== undefined && (
        <div className="mt-6 bg-stone-50 rounded-2xl p-4 space-y-2" data-testid="price-summary">
          <h4 className="text-sm font-semibold text-foreground mb-2">Price Summary</h4>
          {/* Original price */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Class Price</span>
            <span className="text-foreground">
              {pricing.originalPrice && pricing.originalPrice !== pricing.price
                ? `₹${(pricing.originalPrice / 100).toLocaleString()}`
                : pricing.priceDisplay
              }
            </span>
          </div>
          {/* Referral discount — takes precedence over the first-class
              auto-discount when a valid code is applied. */}
          {pricing.referralApplied && pricing.originalPrice && pricing.originalPrice !== pricing.price && (
            <div className="flex items-center justify-between text-sm" data-testid="referral-discount-line">
              <span className="text-purple-700 flex items-center gap-1">
                <Gift className="w-3.5 h-3.5" />
                Referral Discount ({pricing.referralApplied.code} · {pricing.referralApplied.discount_percent}% off)
              </span>
              <span className="text-purple-700">
                -₹{Math.round((pricing.originalPrice - pricing.price) / 100).toLocaleString()}
              </span>
            </div>
          )}
          {/* First class discount line — hidden when a referral or
              coupon supersedes it. */}
          {pricing.isFirstClass && !pricing.referralApplied && !couponApplied && pricing.originalPrice && pricing.originalPrice !== pricing.price && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-600 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> First Class Discount</span>
              <span className="text-green-600">-₹{Math.round((pricing.originalPrice - pricing.price) / 100).toLocaleString()}</span>
            </div>
          )}
          {/* Coupon discount line */}
          {couponApplied && pricing.originalPrice && pricing.originalPrice !== pricing.price && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-600 flex items-center gap-1">
                <Tag className="w-3.5 h-3.5" />
                {pricing.isFirstClass ? 'First Class + Coupon' : `Coupon (${couponApplied.code})`}
              </span>
              <span className="text-green-600">-₹{Math.round((pricing.originalPrice - pricing.price) / 100).toLocaleString()}</span>
            </div>
          )}
          {/* Divider + Total */}
          <div className="border-t border-stone-200 pt-2 mt-2 flex items-center justify-between">
            <span className="font-semibold text-foreground">You Pay</span>
            <span className="text-xl font-bold text-primary" data-testid="final-price">
              {pricing.price === 0 ? 'FREE' : pricing.priceDisplay}
            </span>
          </div>
        </div>
      )}

      {/* CTA — disabled until email + phone are valid */}
      <button
        onClick={handleProceedToPayment}
        disabled={
          isLoading || isSubmitting || pricing.isLoading ||
          !!pricing.payPerClassBlocked ||
          !formData.name.trim() ||
          !isValidEmail(formData.email) ||
          !isValidPhone(formData.phone)
        }
        className="w-full mt-6 px-6 py-4 bg-primary text-white rounded-full font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="guest-pay-btn"
      >
        {isLoading || isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : pricing.price === 0 ? (
          <>
            <CheckCircle className="w-5 h-5" />
            Book Free Class
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pay {pricing.priceDisplay} & Book
          </>
        )}
      </button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        {pricing.isFirstClass 
          ? "We'll create your account automatically after payment"
          : "Payment will be processed securely via Razorpay"
        }
      </p>
    </>
  );

  const renderPayment = () => (
    <div className="text-center py-8">
      <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
      <h3 className="text-lg font-heading text-foreground mb-2">
        Processing Payment
      </h3>
      <p className="text-muted-foreground">
        Please complete payment in the Razorpay window
      </p>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-6">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      
      <h3 className="text-xl font-heading text-foreground mb-2">
        You're Booked!
      </h3>
      
      <p className="text-muted-foreground mb-6">
        {bookingResult?.class_name}<br />
        <span className="text-sm">{bookingResult?.session_time}</span>
      </p>
      
      {bookingResult?.is_new_user && (
        <div className="bg-primary/5 rounded-xl p-4 mb-6 text-left">
          <p className="text-sm text-foreground mb-2">
            <strong>Your account is ready!</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Check your email ({bookingResult?.email}) for login details.
          </p>
        </div>
      )}
      
      <button
        onClick={onClose}
        className="w-full px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all"
      >
        Done
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={step !== 'payment' ? onClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-stone-100 flex items-center justify-between rounded-t-3xl">
          <h2 className="text-lg font-heading text-foreground">
            {step === 'success' ? 'Booking Confirmed' : 'Book Your Class'}
          </h2>
          {step !== 'payment' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>
        
        {/* Content */}
        <div className="p-6">
          {step === 'details' && renderDetails()}
          {step === 'payment' && renderPayment()}
          {step === 'success' && renderSuccess()}
        </div>
      </div>
    </div>
  );
}
